import { BallotSection, BallotSelection, CreateBallotRequest, GetElectionResponse, GetPointResponse, GetPublicOrganizationProductResponse, LoginOrigin, PaymentTargetType, PendingVote, TopicCategory } from '../types';
import { LitElement, css, html } from 'lit';
import { addBallot, electionVotingIsHappening, isVoterEligible } from '../helpers/api/election-api';
import { customElement, property, query, state } from 'lit/decorators';
import { delay, getFromLocalStorage, getIpAddress, isDefined, isValidString, removeLocalStorage, setLocalStorage, undefinedIfEmpty } from '../helpers/utils';
import { login, upgradeToken } from '../helpers/api/account-api';

import { PreCheckoutComponent } from './pre-checkout-component';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { getMainPage } from '../helpers/page-name';
import { getOrganizationProductsPublic } from '../helpers/api/products-api';
import { ifDefined } from 'lit/directives/if-defined';
import { parseDescriptionText } from '../helpers/embeds';
import pointCache from '../helpers/state/point-cache';
import { startStripeCheckoutSession } from '../helpers/api/stripe-api';
import topicData from '../helpers/state/topic-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';

@customElement('election-form-component')
export class ElectionFormComponent extends LitElement {
    static amplitude_callsite: string = 'Election Modal';

    @property({ type: Boolean })
    open: boolean = false;

    @property({ type: Boolean })
    showEmailConfirm: boolean = false;

    @query('#preCheckout')
    private readonly preCheckoutElement?: PreCheckoutComponent | null;



    @query('#electionAgreeement')
    electionAgreeementElement!: HTMLInputElement;

    @query('#email')
    emailComponent?: HTMLInputElement;

    @property ({ type: Boolean })
    previewMode: boolean = false;

    @property ({ type: Boolean })
    refreshForPreview: boolean = false;

    @state()
    voterEligible: boolean = true;

    @state()
    pendingVote?: PendingVote;

    @state()
    focused_point_id?: string;

    @state()
    title: string = '';

    @state()
    step: string = '';

    @state()
    electionTerms: boolean = false;

    @state()
    emailAddress?: string;

    @state()
    oneTimeCode?: string;

    @state()
    votingCategories: TopicCategory[] = [];

    @state()
    election?: GetElectionResponse;

    @state()
    hasActivatedChallenge: boolean = false;

    @state()
    hasLiveElection?: boolean = false;

    @state()
    pendingOpenElection: boolean = false;

    @state()
    currentCategoryIndex?: number;

    @state()
    currentCategory?: TopicCategory;

    @state()
    pointsInCurrentCategory: GetPointResponse[] = [];

    @state()
    selections: PendingVote[] = [];

    @state()
    ballotProducts: GetPublicOrganizationProductResponse[] = [];

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountInfo = new StateController(this, accountInfo);

    async updated(changedProperties: Map<string | number | symbol, unknown>) {
        // if this.title is changed, dispatch event to update modal title
        if (changedProperties.has('title')) {
            this.dispatchEvent(new CustomEvent('on-update-election-modal-title', {
                detail: {
                    title: this.title,
                }
            }));
        }

        if (changedProperties.has('currentCategoryIndex')) {
            if (isDefined(this.currentCategoryIndex)) {
                this.updateCategoryDetails();
            }
        }

        if (changedProperties.has('pendingVote')) {
            if (this.pendingVote) {
                this.orderCategoriesAndSetVote();
            }
        }

        if (changedProperties.has('voterEligible')) {
            if (!this.voterEligible) {
                this.title = 'Come Back Soon!';
                this.step = 'ineligible';
            }
        }

        if (changedProperties.has('election')) {
            if (this.election) {
                await this.startup();
            }
        }

        if (changedProperties.has('refreshForPreview')) {
            if (this.refreshForPreview) {
                this.refreshForPreview = false;
                await this.startup();
            }
        }
    }

    private _lastKnownTotal: string = '';
    private _totalCheckInterval: number | undefined;

    // [mhaughey] Keep up with total cost changes from pre-checkout component
    // Using polling for now since I'm having trouble getting events to work across components.
    // Could revisit later with a different approach if needed.
    connectedCallback() {
        super.connectedCallback();

        // Poll for changes every 500ms
        this._totalCheckInterval = setInterval(() => {
            if (this.preCheckoutElement) {
                const currentTotal = this.preCheckoutElement.totalCostString;
                if (currentTotal !== this._lastKnownTotal) {
                    this._lastKnownTotal = currentTotal;
                    this.requestUpdate();
                }
            }
        }, 500);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (isDefined(this._totalCheckInterval)) {
            clearInterval(this._totalCheckInterval);
        }
    }

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        // if point cache isn't ready, delaty
        if (!pointCache.isReady || !topicData.hasElection) {
            setTimeout(async () => {
                await this.startup();
            }, 1000);
            return;
        }
        this.pendingOpenElection = false;
        this.emailAddress = '';
        this.electionTerms = false;
        this.oneTimeCode = '';
        this.hasLiveElection = false;
        const excludedCategoryIds = this.election?.excluded_categories?.map((ec) => ec.$oid) ?? [];
        this.votingCategories = topicData.categories.filter((c) => c._id && !excludedCategoryIds.includes(c._id.$oid) && pointCache.doesCategoryHavePoints(c));
        this.step = 'election_details';
        this.title = `Time to Vote!`;

        if (!accountInfo.isAuthenticated && this.showEmailConfirm) {
            const signin_modal_election_status = getFromLocalStorage('signin_modal_election');
            if (isDefined(signin_modal_election_status)) {
                const status = JSON.parse(signin_modal_election_status);
                this.emailAddress = status.email ?? '';
                this.hasLiveElection = status.hasLiveElection ?? false;
                this.pendingOpenElection = status.pendingOpenElection ?? false;

                this.title = 'Confirm Email';
                this.step = 'signin_confirm';
            }
        }

        if (topicData.hasElection) {
            this.election = topicData.electionHappeningNow ?? this.election;
            // look for if has previously voted
            if (isDefined(this.pendingVote)) {
                this.orderCategoriesAndSetVote();
            }
            if (this.election) {
                this.voterEligible = this.previewMode ? true : await isVoterEligible(this.election, accountInfo.email ?? '');
                if (!this.voterEligible) {
                    this.title = 'Come Back Soon!';
                    this.step = 'ineligible';
                }
                if (!electionVotingIsHappening(this.election)) {
                    this.title = 'Voting Not Active';
                    this.step = 'not_started';
                }
            }

            await this.getBallotProducts(this.election?._id.$oid ?? '');
        }

    }

    async getBallotProducts(election_id: string) {
        this.ballotProducts = await getOrganizationProductsPublic(PaymentTargetType.Ballot, election_id, topicData.topic?.organization_id?.$oid ?? '');
    }

    setFocusedPoint(point_id: string) {
        this.focused_point_id = point_id;
    }

    resetVotes() {
        this.selections = [];
        this.currentCategoryIndex = undefined;
        this.updateCategoryDetails();
        this.step = 'election_details';
    }

    get descriptionString(): string | undefined {
        if (isValidString(this.election?.description) && isDefined(this.election?.description)) {
            return parseDescriptionText(this.election?.description ?? '', false, false);
        }
        return undefined;
    }

    async highlightPoint(point: GetPointResponse, focused: boolean) {
        await topicData.mapComponent?.setFocusedPoint(new CustomEvent('on-focus-point', { detail: { point, focused: true } }));
    }

    orderCategoriesAndSetVote() {
        const pendingVote = this.pendingVote?.topic_category_id;
        if (this.votingCategories.length > 0 && isDefined(pendingVote)) {
            const pendingCategory = this.votingCategories.find((c) => c._id?.$oid === pendingVote.$oid);
            if (isDefined(pendingCategory)) {
                this.votingCategories = this.votingCategories.filter((c) => c._id?.$oid !== pendingVote.$oid);
                this.votingCategories.unshift(pendingCategory);
            }
            if (this.pendingVote && this.pendingVote.selected_points.length > 0) {
                this.selections = [this.pendingVote];
            }
        }
        this.updateCategoryDetails();
    }

    onChangeEmailAddress(e: any) {
        this.emailAddress = e.detail.value;
    }

    onChangeOneTimeCode(e: any) {
        this.oneTimeCode = e.detail.value;
    }

    async handleGoToElection() {
        this.orderCategoriesAndSetVote();
        this.title = this.election?.name ?? '';
        this.step = 'voting_section';
    }

    handleClickSignin() {
        this.pendingOpenElection = true;
        this.title = this.election?.verify_email ?? true ? 'Verify Email to Vote': 'Enter Your Email to Vote';
        this.step = 'signin_email';
    }

    private openVotingSection() {
        this.title = this.election?.name ?? '';
        this.step = 'voting_section';

        this.orderCategoriesAndSetVote();
    }

    private async handleEmailSubmit() {
        const email = this.emailAddress ?? '';

        if (this.election) {
            topicData.ballotEligible = await isVoterEligible(this.election, email);
            if (!topicData.ballotEligible) {
                this.step = 'ineligible';
                this.title = 'Come Back Soon!';
                return;
            }
        }

        if (this.election?.verify_email === false) {
            if (this.emailComponent?.reportValidity() === true) {
                // check if eligible
                this.openVotingSection();
                return;
            } else {
                getMainPage().errorToastElement.showToastAndThrow('Oops! That doesn’t look like a valid email address.  Try again');
            }
        }

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            await login(ElectionFormComponent.amplitude_callsite, email, LoginOrigin.Election, this.election?.name, topicData.topic?.header_logo);
            const status = {
                email,
                pendingOpenElection: this.pendingOpenElection,
                hasCheckinChallenge: this.hasLiveElection,
                pendingVoteId: isDefined(this.pendingVote) ? this.pendingVote : undefined,
            };

            setLocalStorage('signin_modal_election', JSON.stringify(status));

            this.title = 'Confirm Email';
            this.step = 'signin_confirm';
            this.requestUpdate();
        }, 'Oops! That doesn’t look like a valid email address.  Try again.');
    }

    private handleTryNewEmail() {
        removeLocalStorage('signin_modal_election');
        this.emailAddress = '';
        this.title =  this.election?.verify_email ?? true ? 'Verify Email to Vote' : 'Enter Your Email to Vote';
        this.step = 'signin_email';
    }

    private async handleSubmitOneTimeCode() {
        removeLocalStorage('signin_modal_election');

        const email = this.emailAddress ?? '';
        const oneTimeCode = this.oneTimeCode ?? '';

        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            // Authenticate in the backend with one time code
            await upgradeToken(
                ElectionFormComponent.amplitude_callsite,
                email,
                oneTimeCode,
                undefined,
                LoginOrigin.Election
            );

            this.title = this.election?.name ?? '';
            this.step = 'voting_section';

            this.orderCategoriesAndSetVote();


        }, 'Hmm... That code isn’t right. Try a resending the code or entering a different email address.');
    }

    private async handleSubmitBallot() {
        // Submit the votes
       if (this.previewMode) {
            getMainPage().errorToastElement.showToastAndThrow('You are in preview mode! Submisison will not go through');

       }

        if (!this.election || !topicData.topic) {
            return;
        }

        const ip_address = await getIpAddress();
        const new_ballot: BallotSection[] = [];

        this.selections.forEach((selection) => {
            const category = topicData.categories.find((c) => c._id?.$oid === selection.topic_category_id.$oid);
            if (!category || !category._id) {
                return;
            }

            const selectedPoints: BallotSelection[] = selection.selected_points.map((id) => {
                return {
                    point_id: id,
                    point_name: pointCache.allActivePoints.find((p) => p._id.$oid === id.$oid)?.name ?? '',
                };
            });

            new_ballot.push({
                category_id: category._id,
                selected: selectedPoints,
            });
        });

        if (new_ballot.length === 0) {
            return;
        };

        const ballot: CreateBallotRequest = {
            topic_id: topicData.topic._id,
            election_id: this.election._id,
            email: undefinedIfEmpty(accountInfo.email) ?? this.emailAddress ?? getFromLocalStorage('email') ?? '',
            ip_address,
            email_opt_in: this.electionTerms,
            email_opt_in_language: this.election.email_terms,

            timestamp: new Date().toISOString(),
            ballot: new_ballot
        };

        const paymentRequired = !topicData.isWriter && isDefined(this.preCheckoutElement) && this.preCheckoutElement.remainingLineItems.length > 0;

        if (paymentRequired && isDefined(this.preCheckoutElement)) {
            if (ballot.email === undefined) {
                ballot.email = prompt('Email is required to purchase products. Please enter your email.') ?? '';
                if (ballot.email === undefined) {
                    getMainPage().errorToastElement.showToast('Email is required to submit ballot with payment.');
                    return;
                }
            }

            const response = await startStripeCheckoutSession(ballot.email, this.preCheckoutElement.remainingLineItems, topicData.organizationId ?? '', this.election?._id.$oid, PaymentTargetType.Ballot, ballot, undefined, topicData.topicKey, undefined);

            // open url in same tab
            if (isDefined(response) && isDefined(response.url)) {
                window.location.href = response.url;
            }
        } else {
            // process if no payment required
            try {
                await addBallot(ElectionFormComponent.amplitude_callsite, this.election._id.$oid, ballot);
                // show success message
                getMainPage().errorToastElement.showToast('Thank you for voting!');
                this.title = 'Thank you for voting!';
                this.step = 'success';

                await delay(10000);
                topicData.ballotEligible = false;
                this.dispatchEvent(new CustomEvent('on-close'));
                await this.resetForm();
            } catch (error) {
                console.error('error submitting ballot', error);
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                getMainPage().errorToastElement.showToastAndThrow(`There was an error submitting your ballot. You may have already voted. Please try again later.`);
            }
        }
    }

    private async resetForm() {
        // remove filters
        pointCache.removeFilter();
        this.electionTerms = false;
        this.selections = [];
        this.currentCategoryIndex = undefined;
        this.updateCategoryDetails();
        await this.startup();
    }

    private renderByStep() {
        switch (this.step) {
            case 'election_details':
                return html`
                <div class="terms__desc-wrapper main-description">
                    <b>${this.election?.point_button_text} for ${this.election?.name}</b>
                    <br>
                    ${html`${unsafeHTML(this.descriptionString)}`}
                </div>
                <div class="actions-wrapper column">
                    <mwc-button
                        raised
                        class="action-button"
                        @click="${accountInfo.isAuthenticated ? this.handleGoToElection : this.handleClickSignin}"
                    >
                        ${accountInfo.isAuthenticated ? 'Start Voting' : (this.election?.verify_email ?? true) ? 'Verify Email to Vote' :  'Enter Your Email to Vote'}
                    </mwc-button>
                </div>`;
            case 'signin_email':
                return html`
                    <div class="voting-wrapper">
                        <form-input
                            class="input__email-address"
                            id="email"
                            type="email"
                            label="Email Address"
                            placeholder=""
                            value="${this.emailAddress ?? ''}"
                            @change="${this.onChangeEmailAddress}"
                        >
                        </form-input>
                        ${undefinedIfEmpty(this.election?.email_terms) ? html`
                            <div class="terms__checkbox-wrapper">
                            <input
                                type="checkbox"
                                id="electionAgreeement"
                                name="electionAgreeement"
                                style="accent-color: var(--main-primary-color);"
                                ?checked="${this.electionTerms}"
                                @change="${(e: any) => {
                                    this.electionTerms = !this.electionTerms;
                                }}"
                            />
                            ${this.election?.email_terms}
                            </div>`
                        : ''}
                        <mwc-button
                            raised
                            class="action-button action-button__send-code"
                            ?disabled="${!this.emailAddress || (!this.electionTerms && this.election?.email_opt_in_required === true && isDefined(this.election?.email_terms) && this.election?.email_terms !== '')}"
                            @click="${this.handleEmailSubmit}"
                        >
                            ${this.election?.verify_email ?? true ? 'Get Started' : 'Start Voting'}
                        </mwc-button>
                    </div>
                `;
            case 'signin_confirm':
                return html`
                    <div class="voting-wrapper">
                        <p>
                            Enter the code sent to <b>${this.emailAddress ?? ''}</b>.
                        </p>
                        <form-input
                            class="input__confirm-code"
                            id="code"
                            type="string"
                            label="Access Code"
                            placeholder="000000"
                            value="${this.oneTimeCode ?? ''}"
                            @change="${this.onChangeOneTimeCode}"
                        >
                        </form-input>
                        <mwc-button
                            raised
                            class="action-button action-button__confirm"
                            ?disabled="${!this.oneTimeCode}"
                            @click="${this.handleSubmitOneTimeCode}"
                        >
                            Start Voting
                        </mwc-button>
                        <div class="link-buttons">
                            <p class="link-btn" @click=${this.handleTryNewEmail}>
                                Try Another Email
                            </p>
                            <p class="link-btn" @click=${this.handleEmailSubmit}>
                                Send a New Code
                            </p>
                        </div>
                    </div>
                `;
            case 'voting_section':
                if (this.currentCategoryIndex === undefined) {
                    return this.renderSelectCategory();
                } else {
                    return this.renderCategoryVoting();
                }
                ;
            case 'voting_complete':
                return html`
                    <div class="voting-wrapper">
                        <p>Thank you for voting!</p>
                        ${this.election?.name} allows you to vote ${this.election?.frequency_recurrance}.
                        ${this.renderInviteFriends()}
                    </div>
                `;
            case 'ineligible':
                return html`
                    <div class="voting-wrapper">
                        <p>You've already voted in this election for this period</p>
                        ${this.election?.name} allows you to vote ${this.election?.frequency_recurrance}.
                        <br>
                        ${this.renderInviteFriends()}
                    </div>
                `;
            case 'success':
                return html`
                    <div class="voting-wrapper">
                        <p>Thank you for voting!</p>
                        ${this.election?.name} allows you to vote ${this.election?.frequency_recurrance === 'unlimited' ? 'an unlimited number of times' : this.election?.frequency_recurrance}.
                        ${this.renderInviteFriends()}
                    </div>
                `;
            case 'not_started':
                return html`
                    <div class="voting-wrapper">
                        <p>${this.election?.name} voting is not active at this time.</p>
                    </div>
                `;
            default:
                return '';
        }
    }

    renderInviteFriends() {
        return html`
            <div class="invite-friends">
                <p>Invite friends to vote!</p>
                <copy-link-component
                    text="${this.voteLink}"
                    successMessage="Link copied successfully."
                    textType="Share"
                    pageName="Ballot Page">
                    Copy Link
                </copy-link-component>
            </div>
        `;
    }

    get voteLink() {
        return topicData.swapUrlForOrgUrl(this.election?.link_set?.ballot_link ?? '');
    }

    renderSelectCategory() {
        return html`
            <div class="voting-wrapper">
                <progress-element style="width:100px; margin-bottom:1rem;margin-left:auto;margin-right:auto;" currentIndex="${this.selections.length-1}" totalIndex="${this.votingCategories.length}" barColor=${ifDefined(topicData.topic?.header_color)}></progress-element>
                <div class="select-category-wrapper">
                    ${this.votingCategories.map((c) => html`
                        <div class="select-category-option ${this.selections.some(vote => vote.topic_category_id.$oid === c._id?.$oid) ? 'complete_category' : ''}" @click="${() => this.setCurrentCategory(c._id?.$oid)}">
                            <div class="icon-wrapper">
                                ${this.selections.some(vote => vote.topic_category_id.$oid === c._id?.$oid) ? html`
                                    <mwc-icon class="selected-icon">check_box</mwc-icon>
                                ` : html`
                                    <img src="${c.icon}" alt="${c.name}" style="width: 20px; height: 20px; object-fit: contain;">
                                `}
                            </div>
                            <span>${c.name}</span>
                        </div>
                    `)}
                </div>
                ${this.ballotProducts.length > 0 ? html`
                    <pre-checkout-component
                        id="preCheckout"
                        style="margin-top:1.5rem;"
                        .product_ids=${this.ballotProducts.map((p) => p._id.$oid)}
                        .organization_id=${topicData.topic?.organization_id?.$oid ?? ''}
                        .payment_target_type=${PaymentTargetType.Ballot}
                    >
                    </pre-checkout-component>
                ` : ''}
                <mwc-button
                        raised
                        class="action-button primary-blue"
                        ?disabled="${this.selections.length === 0}"
                        @click="${this.handleSubmitBallot}"
                    >
                        ${this.submitFormText}
                    </mwc-button>
                <p class="description">
                    You may vote ${this.election?.frequency_recurrance === 'unlimited' ? 'unlimited times' : this.election?.frequency_recurrance} in this competition.
                </p>
            </div>
        `;
    }

    get submitFormText() {
        return html`
            ${this.election?.submit_button_text ?? 'Submit'} for ${this.selections.length}/${this.votingCategories.length} ${this.selections.length === 1 ? 'Category' : 'Categories'}
            ${isDefined(this.preCheckoutElement) && this.preCheckoutElement.remainingLineItems.length > 0 ? html`and Pay ${this.preCheckoutElement.totalCostString}` : ''}
        `;
    }

    renderCategoryVoting() {
        return html`
            <div class="voting-wrapper">
                ${undefinedIfEmpty(this.election?.category_descriptions.find((c) => c.category_id === this.currentCategory?._id?.$oid)?.description) ? html`
                    <p class="category-description">
                        ${html`${unsafeHTML(
                            this.election?.category_descriptions.find((c) => c.category_id === this.currentCategory?._id?.$oid)?.description
                        )}`}
                    </p>
                ` : ''}
                ${this.selections.some(vote => vote.topic_category_id.$oid === this.currentCategory?._id?.$oid) ? html`
                    <p class="selection-recorded">Selection Recorded. Return to Categories to Submit Ballot.</p>
                ` : ''}
                <div class="selection-wrapper">
                    ${this.pointsInCurrentCategory.length === 0 ? html`
                        <p>No points to vote on in this category</p>
                    ` : ''}
                    ${this.pointsInCurrentCategory.map((p) => html`
                        <div class="selection-option ${this.focused_point_id === p._id.$oid ? 'focused' : ''}" @click="${() => this.handleSelectionChange(p._id.$oid, this.currentCategory?._id?.$oid)}" @mouseover="${async () => await this.highlightPoint(p,true)}" @mouseout="${async () => await this.highlightPoint(p,false)}">
                            <input
                                type="${this.election?.multi_select === true ? 'checkbox' : 'radio'}"
                                name="electionGroup"
                                style="accent-color: var(--main-primary-color);"
                                id="${p._id.$oid}"
                                .checked="${this.selections.some(vote => vote.selected_points.some(id => id.$oid === p._id.$oid))}"
                            >
                            <span
                                style="font-weight:${this.selections.some(vote => vote.selected_points.some(id => id.$oid === p._id.$oid)) ? '700' : ''}; cursor:pointer;">
                                ${undefinedIfEmpty(p.name) ?? p.location.search.split(',').slice(0, 1)}
                            </span>
                        </div>
                    `)}
                </div>
                <div class="direction-buttons" @click="${() => this.setCurrentCategory(undefined)}">
                    <div class="direction-button-wrapper">
                        <div
                            class="circle-button direction-button primary-blue"
                        >
                            <mwc-icon>west</mwc-icon>
                    </div>
                        Back To Categories
                    </div>
                </div>
            </div>
        `;
    }

    setCurrentCategory(categoryId: string | undefined) {
        if (categoryId === undefined) {
            this.currentCategoryIndex = undefined;
            this.currentCategory = undefined;
            this.pointsInCurrentCategory = [];
            this.title = 'Select a Category to Add Your Vote!';
            // Force total cost check when returning to category selection
            setTimeout(() => {
                if (this.preCheckoutElement) {
                    this._lastKnownTotal = this.preCheckoutElement.totalCostString;
                    this.requestUpdate();
                }
            }, 50);

            this.requestUpdate();
    } else {
        this.currentCategoryIndex = this.votingCategories.findIndex((c) => c._id?.$oid === categoryId);
        this.currentCategory = this.votingCategories.find((c) => c._id?.$oid === categoryId);
            this.pointsInCurrentCategory = pointCache.allActivePoints
                .filter((p) => p.topic_category_id.$oid === categoryId)
                .sort(() => Math.random() - 0.5);
            this.title = this.currentCategory?.name ?? '';
            this.requestUpdate();
        }
    }

    setNextVotingCategory() {
        if (this.currentCategoryIndex === undefined) {
            this.currentCategoryIndex = 0;
        } else {
            this.currentCategoryIndex++;
        }
        this.requestUpdate();
    }

    setPreviousVotingCategory() {
        if (this.currentCategoryIndex === undefined) {
            this.currentCategoryIndex = 0;
        } else {
            this.currentCategoryIndex--;
        }
        this.requestUpdate();
    }

    updateCategoryDetails() {
        if (isDefined(this.currentCategoryIndex) && (this.currentCategoryIndex < this.votingCategories.length)) {
             const category = this.votingCategories[this.currentCategoryIndex ?? 0];
            this.currentCategory = category;

            // Filter points for current category
            const allCategoryPoints = pointCache.allActivePoints
                .filter((p) => p.topic_category_id.$oid === this.currentCategory?._id?.$oid);

            // Single pass separation: focused, featured, and regular points
            const focusedPointId = this.focused_point_id;
            let focusedPoint: GetPointResponse | undefined;
            const featuredPoints: GetPointResponse[] = [];
            const regularPoints: GetPointResponse[] = [];

            allCategoryPoints.forEach(point => {
                if (focusedPointId && point._id.$oid === focusedPointId) {
                    focusedPoint = point;
                } else if (point.featured) {
                    featuredPoints.push(point);
                } else {
                    regularPoints.push(point);
                }
            });

            // Randomize only the regular points (focused and featured maintain their priority)
            regularPoints.sort(() => Math.random() - 0.5);

            // Combine in priority order: focused first, then featured, then randomized regular points
            this.pointsInCurrentCategory = [focusedPoint, ...featuredPoints, ...regularPoints].filter(isDefined);
        }
        if (this.step === 'voting_section') {
            this.title = isDefined(this.currentCategoryIndex) ? this.votingCategories[this.currentCategoryIndex].name : 'Select a Category to Add Your Vote!';
        }
        if (this.step === 'voting_section' && this.currentCategoryIndex !== undefined) {
            const newCategoryName = this.votingCategories[this.currentCategoryIndex ?? 0].name.toLowerCase();
            topicData.mapComponent?.filterCategoriesOnLoad([newCategoryName]);
            this.requestUpdate();
        }
    }

    isPointSelected(pointId: string, categoryId?: string) {
        if (!categoryId) {
            return false;
        }
        const vote = this.selections.find(vote => vote.topic_category_id.$oid === categoryId);
        return vote ? vote.selected_points.some(id => id.$oid === pointId) : false;
    }

    handleSelectionChange(inputId: string, categoryId?: string) {
        if (!categoryId) {
            return;
        }

        let selectionCategory = this.selections.find(vote => vote.topic_category_id.$oid === categoryId);
        const isAlreadySelected = selectionCategory?.selected_points.some(point => point.$oid === inputId);

        if (!isDefined(isAlreadySelected) || !isAlreadySelected ) {
            if (!selectionCategory) {
                selectionCategory = { topic_category_id: { $oid: categoryId }, selected_points: [] };
                this.selections.push(selectionCategory);
            }
            if (this.election?.multi_select !== true) {
                selectionCategory.selected_points = [{ $oid: inputId }];
            } else {
                selectionCategory.selected_points.push({ $oid: inputId });
            }
        } else if (this.election?.multi_select === true && selectionCategory) {
            selectionCategory.selected_points = selectionCategory.selected_points.filter(id => id.$oid !== inputId);
        }
        this.requestUpdate();
    }

    render() {
        return html`
            <div class="modalContent">
                <div class="modal__header">${this.title}</div>
                <div class="modal__content">${this.renderByStep()}</div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`

            .modalContent {
                position: relative;
                height: 100%;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                overflow-y: auto;
                padding: 0 1rem;
                align-items: center;
            }

            .modal__header {
                width: 100%;
                padding: 0px 10px 5px 0px;
                font-size: 18px;
                font-weight: 700;
                line-height: 1.3em;
                box-sizing: border-box;
                text-align:left;
                margin-top: 10px;
            }

            .modal__content {
                padding: 5px 0;
                width: 100%;
                box-sizing: border-box;
                overflow-y: auto;
                display: flex;
                flex-direction: column;
                align-items: stretch;
            }

            .direction-button-wrapper {
                display: flex;
                flex-direction:row;
                align-items: center;
                gap: 5px;
            }

            .selection-option {
                display: flex;
                justify-content: flex-start;
                gap: 5px;
                align-items: center;
                border-bottom: 1px solid var(--theme-border-color);
                padding:5px 10px;
                cursor: pointer;
            }

            .selection-option.focused {
                background-color: var(--main-sky-color);
                margin-bottom: 5px;
            }

            .selection-option:hover {
                font-weight:700;
                background-color: var(--theme-border-color);
                cursor: pointer;
            }

            .select-category-option{
                width: calc(25% - 20px);
                display: flex;
                flex-direction: column;
                background-color: var(--theme-border-color);
                box-shadow: 0 0 5px 0 rgba(0,0,0,0.1);
                cursor: pointer;
                padding: 5px;
                align-items: center;
                justify-content: center;
                line-height: 1;
                text-align: center;
            }

            .complete_category {
                background-color: var(--main-success-color);
                color: var(--theme-white-color);
                box-shadow: unset;
            }

            .select-category-option:hover {
                background-color: var(--main-sky-color);
            }

            .selection-wrapper {
                flex: 1;
                overflow-y: auto;
                width:100%;
            }

            .select-category-wrapper {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                width: 100%;
                align-items: stretch;
                justify-content: flex-start;
                gap: 10px;
                overflow-y: auto;
                flex: 1;
                font-size: 0.675rem;
                overflow-wrap: break-word;
                hyphens: auto;
            }

            .actions-wrapper {
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                gap: 5px;
            }

            .direction-buttons {
                display: flex;
                align-items: center;
                width: 100%;
                gap: 5px;
                justify-content: center;
                flex-wrap: wrap;
                padding-top: 10px;
                border-top : 1px solid var(--main-primary-color);
            }

            .direction-button{
                white-space: nowrap;
            }

            .column {
                flex-direction: column;
            }

            .action-button {
                font-size: 16px;
                border-radius: 8px;
                margin: 10px 0;
                color: var(--theme-white-color);
                --mdc-typography-button-text-transform: capitalize;
            }

            .circle-button {
                font-size: 12px;
                border-radius: 50%;
                border: 1px black solid;
                height: 24px;
                width: 24px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
            }

            .circle-button-placeholder {
                height: 24px;
                width: 24px;
            }

            .circle-button mwc-icon {
                font-size: 14px;
            }

            .primary-blue {
                --btn-background-color: var(--main-primary-color);
                --mdc-theme-primary: var(--main-primary-color);
            }

            .failure-red {
                --mdc-theme-primary: var(--main-invalid-color);
            }

            .terms__desc-wrapper {
                overflow-y: auto;
                box-sizing: border-box;
                line-height: 130%;
                color: var(--theme-input-color);
                margin: 20px 0;
                flex: 1;
            }

            .terms__checkbox-wrapper {
                width: 100%;
                box-sizing: border-box;
                margin: 10px 0;
                text-align: center;
            }

            #electionAgreeement {
                --form-checkbox-label-color: var(--theme-input-color);
            }

            p {
                margin: 5px 0;
            }

            .main-description {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
            }

            .description {
                width: 100%;
                color: var(--theme-input-color);
                margin-bottom: 1px;
            }

            .category-description {
                font-size: 0.75rem;
                font-weight: 700;
                margin-bottom: 10px;
                text-align: left;
            }

            .selection-recorded {
                font-size: 0.75rem;
                color: var(--main-invalid-color);
                margin-bottom: 10px;
                text-align: center;
            }

            .voting-wrapper {
                width: 100%;
                display: flex;
                flex-direction: column;
                justify-content: center;
                overflow-y: auto;
                gap: 5px;
            }

            .flexContainer {
                display: flex;
            }

            .justifySpaceBetween {
                justify-content: space-between;
            }

            .link-buttons {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                margin-top: 20px;
            }

            .link-btn {
                cursor: pointer;
                text-decoration: underline;
                line-height: 125%;
                color: var(--theme-input-color);
            }

            .email-address {
                color: var(--theme-input-color);
            }

            .action-button__send-code {
                width: 100%;
                max-width: 240px;
                margin: 20px 0;
            }

            .action-button__confirm {
                width: 100%;
                max-width: 250px;
                margin: 15px 0;
            }

            .input__email-address {
                width: 100%;
                max-width: 240px;
                margin-top: 15px;
                text-align: center;
            }

            .input__confirm-code,
            .input__user-name {
                width: 100%;
                max-width: 250px;
                margin: 5px 0;
                text-align: center;
            }

            @media only screen and (max-width: 550px) {
                .modalContent {
                    width: 100%;
                    min-height: 100%;
                    max-height: 100%;
                    padding: 0;
                }
                .select-category-option{
                    width: calc(50% - 20px);
                }

                .modal__header {
                    font-size: 14px;
                }

                .terms__desc-wrapper {
                    max-height: 200px;
                }
            }
        `;
    }
}


